import { FC, useEffect, useState, memo } from 'react';
import { noop } from 'lodash';
import { useCopyToClipboard } from 'react-use';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { Tooltip } from 'components/PlatformResourceSelector/Tooltip';
import SvgCopy02Sm from 'modules/Playground/Components/Icons/Copy02Sm';
import { styles } from './styles';

export type ShareButtonProps = {
  /** URL to be copied when clicking the copy button. Defaults to current window URL */
  copyUrl?: string;
  /** Text to show in the copy button tooltip */
  copyTooltipText?: string;
  /** Callback triggered after successful copy */
  onCopy?: () => void;
  /** Custom class name for the container */
  className?: string;
  /** Controls visibility of the copy button */
  showCopyButton?: boolean;
  /** Custom success message shown after copying */
  copySuccessMessage?: string;
};

const defaultProps = {
  copyUrl: typeof window !== 'undefined' ? window.location.href : '',
  copyTooltipText: 'Copy URL',
  showCopyButton: true,
  copySuccessMessage: 'URL copied to clipboard',
} as const;

export const ShareButton: FC<ShareButtonProps> = memo(
  ({
    copyUrl = defaultProps.copyUrl,
    copyTooltipText = defaultProps.copyTooltipText,
    onCopy,
    className,
    showCopyButton = defaultProps.showCopyButton,
    copySuccessMessage = defaultProps.copySuccessMessage,
  }) => {
    const [copyLink, setCopyLink] = useState(copyUrl);
    const [, copyToClipboard] = useCopyToClipboard();

    useEffect(() => {
      setCopyLink(copyUrl);
    }, [copyUrl]);

    const handleCopyClick = () => {
      copyToClipboard(copyLink);
      cogoToast.info(copySuccessMessage);
      onCopy?.();
    };

    if (!showCopyButton) {
      return null;
    }

    return (
      <Tooltip text={copyTooltipText} initialState={{ placement: 'bottom' }}>
        <button type="button" className={`${styles.button} ${className || ''}`} onClick={handleCopyClick} onKeyDown={noop} tabIndex={0}>
          <SvgCopy02Sm />
        </button>
      </Tooltip>
    );
  },
);

ShareButton.displayName = 'ShareButton';

export default ShareButton;
